import React from "react";
import { useLocation } from '@reach/router';
import { Link } from "gatsby";
import { menuItems } from "@/data/menuItems";
import UserForm from '@/components/form/EOI-Form';
import { Facebook, Linkedin, Instagram, Twitter } from 'lucide-react';
import { KeyDates } from "@/components/KeyDates";
import { StaticImage } from "gatsby-plugin-image";
import config from '@/utils/config';
import ScrollRevealWrapper from '@/components/utility/ScrollRevealWrapper';

const iconMap = {
  facebook: <Facebook strokeWidth={2} size={30} />,
  linkedin: <Linkedin strokeWidth={1} size={30} />,
  instagram: <Instagram strokeWidth={1} size={30} />,
  twitter: <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512" className="invert w-[30px] h-[30px]"><path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"/></svg>,
};

const FooterLinkSection = ({ label, items }) => (
  <div className="space-y-4">
    <h4 className="text-lg font-bold mt-0">{label}</h4>
    <ul className="text-neutral-100">
      {items.map((link, index) => (
        <li key={index}>
          <Link to={link.to}>{link.label}</Link>
        </li>
      ))}
    </ul>
  </div>
);

export default function Footer({ data }) {
  const eventName = 'AusACPDM 2026';
  const location = useLocation();
  const currentPath = location.pathname;

  const normalizePath = (path) => (path.endsWith('/') ? path : `${path}/`);
  const normalizedCurrentPath = normalizePath(currentPath);
  const normalizedCustomPages = config.sidebar.customPages.map(normalizePath);

  const isSidebarActiveForPage =
    config.sidebar.enabled &&
    normalizedCustomPages.includes(normalizedCurrentPath);

  const shouldRenderFooterSection = !isSidebarActiveForPage;
  const VenuePage = currentPath === '/conference-venue/';

  return (
    <footer>

      {VenuePage && (
        <iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d2923.6200483410526!2d147.3309669!3d-42.8808631!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xaa6e758395fa2187%3A0x673852a8ad0e2d82!2sHotel%20Grand%20Chancellor%20Hobart!5e0!3m2!1sen!2sau!4v1740369992840!5m2!1sen!2sau" width="100%" height="450" className="border-0" title="Grand Chancellor Hotel" allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
      )}

      {shouldRenderFooterSection && (
      <section className="bg-c2">
        <div className="container px-4 md:px-6 py-12 md:py-16">
          <h2 className="text-center mt-0 mb-10 text-white">Key Dates</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-white">
            <KeyDates data={data} />
          </div>
        </div>
      </section>
      )}

      <section className="hard-gradient-section bg-c5 lg:bg-[linear-gradient(to_right,var(--tw-color-c5)_50%,var(--tw-color-c4)_50%)]">
        <div className="container px-0">
          <div className="flex items-center justify-center w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-center justify-center w-full rounded-lg">
              <div>
                <ScrollRevealWrapper>
                  <div className="py-10 px-6 dark-theme" id="mailing-list">
                    <h2 className="mb-4 text-white">Join the Mailing List</h2>
                    <UserForm eventName={eventName} />
                  </div>
                </ScrollRevealWrapper>
              </div>
              <div className="bg-c4 px-6 h-full flex items-center">
                <ScrollRevealWrapper delay={250}>
                  <div className="py-10 h-full" id="aoc">
                    <div>
                      <h2 className="text-white">Acknowledgement of Country</h2>
                      <p className="text-slate-100">AusACPDM 2026 acknowledges Aboriginal and Torres Strait Islander peoples as the Traditional Custodians of the land and sea in which we live and work. We recognise their continuing connection to land, sea and culture and pay our respects to Elders past, present and future. In particular we pay our respects to the Muwinina and Palawa people, their elders past and present, Traditional Custodians of the lands where our conference will be held.</p>
                    </div>
                    <div className="flex gap-3 items-center mt-6">
                      <StaticImage
                        src="../images/indigenous-flag.webp"
                        alt="Indigenous Flag"
                        width={150}
                        loading="lazy"
                        placeholder="none"
                        formats={["auto", "webp"]}
                      />
                      <StaticImage
                        src="../images/torres-stait.webp"
                        alt="Torres Strait Flag"
                        width={150}
                        loading="lazy"
                        placeholder="none"
                        formats={["auto", "webp"]}
                      />
                    </div>
                  </div>
                </ScrollRevealWrapper>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-c5/90">
        <div className="container mx-auto px-4 md:px-6 py-12 grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-8 text-white">
          {menuItems.footer.map((section, index) => (
            <ScrollRevealWrapper key={index} delay={100 * index}>
              <FooterLinkSection label={section.label} items={section.items} />
            </ScrollRevealWrapper>
          ))}
        </div>
      </section>

      <section className="bg-c5">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <div className="flex flex-col lg:flex-row justify-between gap-3 py-6">
            <div className="order-2 lg:order-1 text-slate-100">© {new Date().getFullYear()} <a href="https://icmsaust.com.au/" target="_blank" rel="noopener noreferrer" className="text-dteal">ICMSA</a>. All rights reserved. | <a href="https://icmsaust.com.au/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-dteal">Privacy Policy</a></div>
            <div className="flex flex-row gap-3 order-1 lg:order-2 justify-center items-end">
              {menuItems.social.map((social, index) => (
                <a
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={index}
                  className="text-white hover:text-dteal flex items-center"
                  aria-label={social.screenReader}
                >
                  {iconMap[social.name.toLowerCase()]}
                  <span className="sr-only">{social.screenReader}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </footer>
  );
}
